import React, { useEffect, useState } from 'react';

import { Paper, Stack } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ChecklistRuleCard from '../../reportCards/ChecklistRuleCard';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import type { ManualOverrideState } from '../../types';

import type { SetupChecklistRow } from './types';

interface Props {
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<SetupChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function SetupReportCard({
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpandedRows(rows.reduce<Record<string, boolean>>((nextRows, row) => ({ ...nextRows, [row.id]: true }), {}));
  }, [rows]);

  return (
    <Stack spacing={1.5}>
      {rows.length === 0 ? (
        <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
          No setup rows match this view.
        </Paper>
      ) : (
        rows.map((row) => {
          const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
          const approved = isRowApproved(row, manualOverrides);
          const isExpanded = expandedRows[row.id] ?? true;

          return (
            <ChecklistRuleCard
              key={row.id}
              approved={approved}
              dataPoints={row.evidence.map((evidence) => ({
                error: evidence.error,
                label: evidence.label,
                readOnly: true,
                value: getInputValue(evidence),
              }))}
              dataPointsLabel="Validation inputs"
              expanded={isExpanded}
              manuallyApproved={manuallyApproved}
              rule={row.rule}
              summary={row.summary}
              title={row.field}
              onApprovalChange={() => onApprovalChange(row.id)}
              onUnapprovalChange={() => onUnapprovalChange(row.id)}
              onToggleExpanded={() => setExpandedRows((currentRows) => ({ ...currentRows, [row.id]: !isExpanded }))}
            />
          );
        })
      )}
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="setup row" showAutoApproved={showAutoApproved} />
    </Stack>
  );
}

function getInputValue(evidence: SetupChecklistRow['evidence'][number]) {
  return evidence.canOverride && evidence.value === '-' ? '' : evidence.value;
}
